/**
 * TasksTab — Tab 5 des Card-Detail-Modal (Phase 4 Plan 01, TASK-01..05).
 *
 * Verantwortlich fuer:
 *  - Liste aller Tasks fuer den aktiven Lead (offene oben, abgehakte unten)
 *  - Add-Form (Titel + Datum-Picker, Default = heute)
 *  - Optimistic-Create via store.createTaskOptimistic
 *  - Checkbox zum Abhaken via store.setTaskCompletedOptimistic
 *  - Backend setzt LeadEvent('task_created'|'task_completed') automatisch
 *
 * CI-Farben: #E9FF57 (Action-Button + heute-Badge) + #1A1A1A (Text) — niemals
 * weisse Schrift auf gelb.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { CheckSquare, Square } from 'lucide-react';
import { useLeadBoard } from '../../store/useLeadBoard';
import type { Lead, LeadTask } from '../../types';

interface Props {
  lead: Lead;
  onSaved?: () => void;
  onDirty?: (dirty: boolean) => void;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Formatiert dueDate (yyyy-MM-dd) als "Heute" / "Morgen" / "Gestern" /
 * "Mi 27.05.". Pure helper — nimmt now als Argument fuer testbare Zeit.
 */
function formatDueDate(dueDate: string | null, now: Date = new Date()): string {
  if (!dueDate) return '—';
  const todayStr = now.toISOString().slice(0, 10);
  if (dueDate === todayStr) return 'Heute';
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (dueDate === yesterday.toISOString().slice(0, 10)) return 'Gestern';
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (dueDate === tomorrow.toISOString().slice(0, 10)) return 'Morgen';
  const d = new Date(dueDate + 'T12:00:00Z');
  const weekday = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][d.getUTCDay()];
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${weekday} ${dd}.${mm}.`;
}

/**
 * Tasks sortieren: offene zuerst (nach dueDate ASC, ueberfaellige zuerst),
 * dann abgehakte (nach completedAt DESC = neueste zuerst).
 */
function sortTasks(tasks: LeadTask[]): LeadTask[] {
  const open = tasks
    .filter((t) => !t.completedAt)
    .sort((a, b) => (a.dueDate < b.dueDate ? -1 : a.dueDate > b.dueDate ? 1 : 0));
  const done = tasks
    .filter((t) => t.completedAt)
    .sort((a, b) => {
      if (!a.completedAt || !b.completedAt) return 0;
      return a.completedAt < b.completedAt ? 1 : -1;
    });
  return [...open, ...done];
}

export default function TasksTab({ lead, onSaved, onDirty }: Props) {
  const tasks = useLeadBoard((s) => s.tasksByLead[lead.id]) ?? [];
  const loadTasksForLead = useLeadBoard((s) => s.loadTasksForLead);
  const createTaskOptimistic = useLeadBoard((s) => s.createTaskOptimistic);
  const setTaskCompletedOptimistic = useLeadBoard(
    (s) => s.setTaskCompletedOptimistic,
  );

  const [title, setTitle] = useState('');
  const [dueDate, setDueDate] = useState<string>(todayIso());
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [busyTaskIds, setBusyTaskIds] = useState<Set<number>>(new Set());

  // Tasks initial laden bei Lead-Wechsel
  useEffect(() => {
    loadTasksForLead(lead.id).catch(() => undefined);
    setTitle('');
    setDueDate(todayIso());
    setFormError(null);
  }, [lead.id, loadTasksForLead]);

  // dirty-Flag fuer Modal-Backdrop-Confirm — Form gilt als dirty wenn Titel
  // nicht leer ist
  useEffect(() => {
    onDirty?.(title.trim().length > 0);
  }, [title, onDirty]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = title.trim();
    if (!trimmed) {
      setFormError('Titel ist Pflicht');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) {
      setFormError('Datum erforderlich (YYYY-MM-DD)');
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      await createTaskOptimistic(lead.id, { title: trimmed, dueDate });
      setTitle('');
      setDueDate(todayIso());
      onDirty?.(false);
      onSaved?.();
    } catch (err: any) {
      setFormError(err?.message ?? 'Task konnte nicht angelegt werden');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggle = async (task: LeadTask) => {
    const next = !task.completedAt;
    setBusyTaskIds((s) => {
      const n = new Set(s);
      n.add(task.id);
      return n;
    });
    try {
      await setTaskCompletedOptimistic(task.id, lead.id, next);
      onSaved?.();
    } catch {
      // Fehler ist bereits im Store-Error landet — Lokal nichts weiter tun
    } finally {
      setBusyTaskIds((s) => {
        const n = new Set(s);
        n.delete(task.id);
        return n;
      });
    }
  };

  const sorted = sortTasks(tasks);
  const today = todayIso();

  return (
    <div className="space-y-6">
      {/* Section: Add-Form */}
      <section>
        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2">
          Neuen Task hinzufuegen
        </h3>
        <form onSubmit={handleSubmit} className="space-y-2">
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z.B. Rueckruf bei Verein"
              maxLength={250}
              className="flex-1 border border-[#DAD9D9] rounded px-3 py-1.5 text-sm text-[#1A1A1A]"
            />
            <input
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="border border-[#DAD9D9] rounded px-3 py-1.5 text-sm text-[#1A1A1A]"
            />
            <button
              type="submit"
              disabled={submitting || title.trim().length === 0}
              className="bg-[#E9FF57] text-[#1A1A1A] px-4 py-1.5 rounded text-sm font-medium hover:bg-[#d4ea4d] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Speichert...' : 'Hinzufuegen'}
            </button>
          </div>
          {formError && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              {formError}
            </div>
          )}
        </form>
      </section>

      {/* Section: Task-Liste */}
      <section className="border-t border-[#DAD9D9] pt-4">
        <h3 className="text-sm font-semibold text-[#1A1A1A] mb-2">
          Tasks ({tasks.filter((t) => !t.completedAt).length} offen,{' '}
          {tasks.filter((t) => t.completedAt).length} erledigt)
        </h3>

        {sorted.length === 0 && (
          <div className="text-sm text-gray-500 italic">
            Noch keine Tasks fuer diesen Lead.
          </div>
        )}

        <ul className="space-y-1.5">
          {sorted.map((task) => {
            const done = Boolean(task.completedAt);
            const overdue = !done && task.dueDate < today;
            const isToday = !done && task.dueDate === today;
            const busy = busyTaskIds.has(task.id);
            return (
              <li
                key={task.id}
                className="flex items-center gap-3 px-2 py-1.5 rounded hover:bg-gray-50"
              >
                <button
                  type="button"
                  onClick={() => handleToggle(task)}
                  disabled={busy}
                  className="text-[#1A1A1A] hover:text-[#E9FF57] disabled:opacity-50 shrink-0"
                  aria-label={done ? 'Task wieder oeffnen' : 'Task abhaken'}
                  aria-pressed={done}
                >
                  {done ? (
                    <CheckSquare className="w-5 h-5 text-green-600" />
                  ) : (
                    <Square className="w-5 h-5" />
                  )}
                </button>
                <span
                  className={
                    done
                      ? 'flex-1 text-sm text-gray-400 line-through truncate'
                      : 'flex-1 text-sm text-[#1A1A1A] truncate'
                  }
                >
                  {task.title}
                </span>
                <span
                  className={
                    done
                      ? 'text-xs text-gray-400 shrink-0'
                      : overdue
                        ? 'text-xs font-medium text-red-600 shrink-0'
                        : isToday
                          ? 'text-xs font-medium bg-[#E9FF57] text-[#1A1A1A] px-2 py-0.5 rounded shrink-0'
                          : 'text-xs text-gray-500 shrink-0'
                  }
                >
                  {formatDueDate(task.dueDate)}
                </span>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
